import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { Box, IconButton, Slider, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { MediaMetadata } from "src/config/store/services/media-api/types";
import { useGlobalEvent } from "src/modules/shared/hooks/use-global-event";
import { PlayerContext } from "../media-player";
import { TagMarkers } from "./tag-markers/tag-markers";
import { useVideoPlayerControls } from "./use-video-player-controls";
import { bufferProgress, classes, sliderRail } from "./video-controls.styles";

type ControlsProps = {
  idTags?: MediaMetadata["tags"];
};
export type ControlsMethods = {
  handleOnTimeUpdate: () => void;
  handleBuffer: () => void;
};

const formatDuration = (ms: number) => {
  return dayjs(ms * 1000).format("m:ss");
};

const ControlsActions = memo(() => {
  const { videoElement, isPlaying, togglePlay } = useContext(PlayerContext);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(100);

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleMute2 = (e: KeyboardEvent) => {
    if (e.code === "KeyM") {
      toggleMute();
    }
  };

  useGlobalEvent(toggleMute2, "keydown");

  const handleVolumeChange = useCallback(
    (value: number) => {
      videoElement.volume = value / 100;
    },
    [videoElement]
  );

  const handleChange = (value: number) => {
    if (isMuted) {
      setIsMuted(false);
    }

    if (value === 0) {
      setIsMuted(true);
    }

    handleVolumeChange(value as number);
    setVolume(value as number);
  };

  useEffect(() => {
    isMuted ? (videoElement.muted = true) : (videoElement.muted = false);
  }, [videoElement, isMuted]);

  return (
    <Box sx={classes.actions}>
      <Tooltip title={isPlaying ? "Pause" : "Play"} placement="top">
        <IconButton
          color="primary"
          sx={classes.controlIcon}
          onClick={togglePlay}
        >
          {!isPlaying ? <PlayArrowIcon /> : <PauseIcon />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Toggle mute" placement="top">
        <IconButton
          color="primary"
          sx={classes.controlIcon}
          className="mute-btn"
          onClick={toggleMute}
        >
          {!isMuted ? <VolumeUpIcon /> : <VolumeOffIcon />}
        </IconButton>
      </Tooltip>

      <Box sx={classes.volume} className="volume">
        <Slider
          sx={classes.progressBar}
          size="small"
          value={isMuted ? 0 : volume}
          onChange={(event, value) => {
            handleChange(value as number);
          }}
        />
      </Box>
    </Box>
  );
});

const Timer = () => {
  const { videoElement } = useVideoPlayerControls();

  const [currentTime, setCurrentTime] = useState("0:00");

  const handleTimeUpdate = useCallback(() => {
    setCurrentTime(formatDuration(videoElement.currentTime));
  }, [videoElement.currentTime]);

  useEffect(() => {
    if (videoElement) {
      videoElement.addEventListener("timeupdate", handleTimeUpdate);
    }

    return () =>
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
  }, [videoElement, handleTimeUpdate]);

  return (
    <Box sx={classes.timeBox}>
      {currentTime} / {formatDuration(videoElement.duration)}
    </Box>
  );
};

const VideoControls = forwardRef(({ idTags }: ControlsProps, ref) => {
  const {
    handleOnTimeUpdate,
    handleManualChange,
    handleSeekMouseDown,
    handleSeekMouseUp,
    handleBuffer,
    buffer,
    videoElement,
    sliderRef,
  } = useVideoPlayerControls();

  const { toggleFullScreen } = useContext(PlayerContext);

  useImperativeHandle(ref, () => ({
    handleOnTimeUpdate,
    handleBuffer,
  }));

  return (
    <Box className={"controls-wrapper"} sx={classes.controlsWrapper}>
      {idTags && (
        <TagMarkers
          tags={idTags}
          video={videoElement}
          onClick={handleManualChange}
        />
      )}

      <Slider
        sx={{
          ...classes.progressBar,
          ".MuiSlider-rail": {
            ...sliderRail,

            "::after": {
              ...bufferProgress,
              width: buffer + "%",
            },
          },
        }}
        aria-label="progress"
        size="small"
        onMouseDown={handleSeekMouseDown}
        onMouseUp={handleSeekMouseUp}
        ref={sliderRef}
        onChange={(event, value) => {
          handleManualChange(value as number);
        }}
      />

      <Box sx={classes.actionsBar}>
        <ControlsActions />

        <Timer />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* <Tooltip title={showTags ? "Hide tags" : "Show tags"} placement="top">
            <Box sx={{ display: "flex", padding: 1 }}>
              <CustomSwitch
                size="small"
                checked={showTags}
                onChange={toggleTags}
              />
            </Box>
          </Tooltip> */}

          <Tooltip title="Fullscreen" placement="top">
            <IconButton
              color="primary"
              sx={classes.controlIcon}
              onClick={toggleFullScreen}
            >
              {document.fullscreenElement ? (
                <FullscreenExitIcon />
              ) : (
                <FullscreenIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
});

export default VideoControls;
